import { isChunkLoadError } from '@shared/core/chunkRetry';
import { addAction, addError } from '@shared/utils/logger';
import { withWindow } from '@shared/utils/withWindow';
import type { AuthCallback, AuthError } from './auth.types';
import type { AuthFailureCode, AuthSetupError } from './authFailure.types';

const AUTH_LIBRARY_LOAD_FAILED: AuthFailureCode = 'auth_library_load_failed';
const AUTH_CLIENT_FAILED: AuthFailureCode = 'auth_client_failed';

const errorNameOf = (error: unknown): string => {
  const name = (error as { name?: unknown } | null | undefined)?.name;
  return typeof name === 'string' ? name : 'unknown';
};

// No `description`: several sign-in forms render `err.description` verbatim, and there is no
// translated copy for this case yet.
const toAuthError = (code: AuthFailureCode, error: unknown): AuthSetupError => ({
  error: code,
  code,
  name: errorNameOf(error),
  errorDescription: code === AUTH_LIBRARY_LOAD_FAILED ? 'auth0-js could not be loaded' : 'auth0-js threw while running an auth flow',
  original: error,
});

/** True for the errors this module hands to auth callbacks: auth0-js failed to load or to run. */
export const isAuthSetupError = (error: unknown): error is AuthSetupError => {
  if (typeof error !== 'object' || error === null || !('original' in error)) return false;
  const { code } = error as { code?: unknown };
  return code === AUTH_LIBRARY_LOAD_FAILED || code === AUTH_CLIENT_FAILED;
};

const asAuthSetupError = (failure: unknown): AuthSetupError => (isAuthSetupError(failure) ? failure : toAuthError(AUTH_CLIENT_FAILED, failure));

export const toLoadFailure = (error: unknown): AuthSetupError => toAuthError(AUTH_LIBRARY_LOAD_FAILED, error);

// Telemetry runs inside promise handlers; if it throws, the flow must still finish and no
// rejection may be left behind.
const safely = (report: () => void): void => {
  try {
    report();
  } catch {
    // Nothing useful left to report to.
  }
};

export const recordAuthFailure = (flow: string, failure: unknown): void =>
  safely(() => {
    const authError = asAuthSetupError(failure);
    if (authError.code === AUTH_LIBRARY_LOAD_FAILED) {
      addAction('authLibraryLoadFailed', { flow, errorName: authError.name });
      // chunkRetry records chunk failures (ChunkRetryExhausted) but rethrows anything else with no
      // telemetry, e.g. auth0-js throwing while its module evaluates.
      if (!isChunkLoadError(authError.original)) addError(authError.original, { authFlow: flow, authFailure: authError.code });
      return;
    }
    addError(authError.original, { authFlow: flow, authFailure: authError.code });
  });

/**
 * Caches a successful result and forgets a failed one, so the next call retries. Concurrent
 * callers share the in-flight attempt.
 */
export const retryUntilLoaded = <T>(create: () => Promise<T>): (() => Promise<T>) => {
  let pending: Promise<T> | null = null;
  return () => {
    if (!pending) {
      // Through `then` so a synchronous throw from `create` becomes a rejection, not an exception.
      pending = Promise.resolve()
        .then(create)
        .catch((error: unknown) => {
          pending = null;
          throw error;
        });
    }
    return pending;
  };
};

/** A redirect flow (authorize, social login, logout): failures are recorded, there is no caller to tell. */
export const runRedirectFlow = <C>(flow: string, getClient: () => Promise<C>, run: (client: C) => void): void => {
  getClient()
    .then(run)
    .catch((failure: unknown) => recordAuthFailure(flow, failure));
};

/**
 * A flow that reports through `cb`. `cb` runs exactly once, including when auth0-js calls it and
 * then throws. An exception from `cb` itself is the caller's bug, not an auth failure: it is
 * reported on its own and kept out of this chain, so it is never labelled `auth_client_failed`
 * or left as an unhandled rejection.
 */
export const runCallbackFlow = <C, R>(
  flow: string,
  getClient: () => Promise<C>,
  cb: AuthCallback<R, AuthError>,
  run: (client: C, done: AuthCallback<R, AuthError>) => void,
): void => {
  let called = false;
  const done: AuthCallback<R, AuthError> = (error, result) => {
    if (called) return;
    called = true;
    try {
      cb(error, result);
    } catch (callbackError) {
      safely(() => addError(callbackError, { authFlow: flow, authFailure: 'callback_threw' }));
    }
  };
  getClient()
    .then(client => run(client, done))
    .catch((failure: unknown) => {
      done(asAuthSetupError(failure), null as R);
      recordAuthFailure(flow, failure);
    });
};

// Only these first path segments are recorded as-is. Any other first segment can be an event
// handle (`/alice-and-bob/collaborators`), which must not reach telemetry, so it is bucketed.
const KNOWN_ENTRY_PAGES = new Set([
  'account',
  'account-callback',
  'account-login',
  'account-redirect',
  'card',
  'cards',
  'createbabyregistry',
  'createevent',
  'createwedding',
  'login',
  'servicecenter',
  'social-account',
  'social-login',
]);
const EVENT_SUBPAGES = new Set(['collaborators', 'edit']);

/** The page that started an auth flow, from a fixed list (never the query string or an event handle). */
export const entryPoint = (): string =>
  withWindow(window => {
    const [first, second] = window.location.pathname.split('/').slice(1);
    if (!first) return 'root';
    if (KNOWN_ENTRY_PAGES.has(first)) return first;
    if (second && EVENT_SUBPAGES.has(second)) return `event/${second}`;
    return 'other';
  }, 'ssr');
